import { useGetMentorPerformance } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  ExclamationIcon,
  StarIcon,
  ThumbUpIcon,
  UserGroupIcon,
} from '@heroicons/react/outline';
import {
  EmptyState,
  FilterField,
  MetricCard,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import DetailsModal from '@modules/Admin/components/DetailsModal/DetailsModal';
import useDetailsModal from '@modules/Admin/hooks/useDetailsModal';
import {
  MentorPerformanceBand,
  MentorPerformanceDTO,
  MentorPerformanceDriverDTO,
  MentorPerformanceDriverKey,
  MentorPerformanceEvidenceDTO,
  MentorPerformanceTone,
} from '@shared/apis/mentorPerformance.apis';
import { FLAG_TYPES, MENTOR_FLAG_IMPACT, MENTOR_FLAG_IMPACT_NOTES } from '@shared/constants';
import { FlagTypes } from '@shared/enums';
import AgoraSearchBar from '@utils/AgoraSearchbar';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';
import { Fragment, useMemo, useState } from 'react';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top transition-colors';
const leftHead = 'px-4 py-3 text-left font-semibold';
const rightHead = 'px-4 py-3 text-right font-semibold';
const leftCell = 'px-4 py-3 text-left text-customLight';
const rightCell = 'px-4 py-3 text-right tabular-nums text-customLight';

const WINDOW_OPTIONS = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last 180 days', value: 180 },
  { label: 'Last 365 days', value: 365 },
];

const BAND_OPTIONS: { label: string; value: MentorPerformanceBand | 'unscored' | 'all' }[] = [
  { label: 'All mentors', value: 'all' },
  { label: 'At risk', value: 'at_risk' },
  { label: 'Needs attention', value: 'attention' },
  { label: 'Good', value: 'good' },
  { label: 'Excellent', value: 'excellent' },
  { label: 'Not enough data', value: 'unscored' },
];

const STATUS_OPTIONS = [
  { label: 'Any status', value: 'all' },
  { label: 'Active', value: 'Active' },
  { label: 'In Training', value: 'In Training' },
  { label: 'Under Observation', value: 'Under Observation' },
  { label: 'Leaving', value: 'Leaving' },
  { label: 'Inactive', value: 'Inactive' },
];

const MIN_SESSION_OPTIONS = [
  { label: 'Any activity', value: 0 },
  { label: '3+ sessions', value: 3 },
  { label: '5+ sessions', value: 5 },
  { label: '10+ sessions', value: 10 },
];

type SortKey = 'score' | 'name' | 'sessions' | 'students' | 'feedback' | 'parent' | 'ai';

const SORT_OPTIONS: { label: string; value: SortKey }[] = [
  { label: 'Score, lowest first', value: 'score' },
  { label: 'Name', value: 'name' },
  { label: 'Sessions held', value: 'sessions' },
  { label: 'Active students', value: 'students' },
  { label: 'Feedback on time', value: 'feedback' },
  { label: 'Parent rating', value: 'parent' },
  { label: 'AI quality', value: 'ai' },
];

const BAND_STYLES: Record<MentorPerformanceBand, { label: string; className: string }> = {
  excellent: { label: 'Excellent', className: 'bg-customGreen/15 text-customGreen' },
  good: { label: 'Good', className: 'bg-customPrimary/15 text-customPrimary' },
  attention: { label: 'Needs attention', className: 'bg-customGold/15 text-customGold' },
  at_risk: { label: 'At risk', className: 'bg-customRed/15 text-customRed' },
};

const TONE_TEXT: Record<MentorPerformanceTone, string> = {
  good: 'text-customGreen',
  ok: 'text-customGold',
  bad: 'text-customRed',
  neutral: 'text-customGrey',
};

const TONE_BAR: Record<MentorPerformanceTone, string> = {
  good: 'bg-customGreen',
  ok: 'bg-customGold',
  bad: 'bg-customRed',
  neutral: 'bg-white/20',
};

const formatPct = (value?: number) => (typeof value === 'number' ? `${value}%` : '—');
const formatRating = (value?: number) => (typeof value === 'number' ? value.toFixed(1) : '—');

const ScoreBadge = (props: { score: number | null; band: MentorPerformanceBand | null }) => {
  if (props.score === null || !props.band) {
    return (
      <span className="inline-flex items-center rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-customGrey">
        Not enough data
      </span>
    );
  }

  const style = BAND_STYLES[props.band];

  return (
    <span
      className={mergeClassNames(
        'inline-flex items-center gap-2 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold',
        style.className
      )}
    >
      <span className="text-sm tabular-nums">{props.score}</span>
      <span>{style.label}</span>
    </span>
  );
};

const FLAG_WEIGHT_ROWS = (Object.keys(MENTOR_FLAG_IMPACT) as unknown as string[])
  .map(Number)
  .map((flagType: FlagTypes) => ({
    flagType,
    label: FLAG_TYPES[flagType] ?? `Flag ${flagType}`,
    weight: MENTOR_FLAG_IMPACT[flagType],
    note: MENTOR_FLAG_IMPACT_NOTES[flagType],
  }))
  .sort((a, b) => b.weight - a.weight || a.label.localeCompare(b.label));

const FlagWeightsLegend = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-5">
      <button
        type="button"
        className="flex items-center justify-between text-left"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-white">How flags are weighted</span>
          <span className="text-xs text-customGrey">
            Every flag raised on a mentor's enrollments costs impact points by severity. The total
            is divided by the mentor's students and normalised to 90 days.
          </span>
        </span>
        {isOpen ? (
          <ChevronUpIcon className="h-4 w-4 shrink-0 text-customGrey" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-customGrey" />
        )}
      </button>
      {isOpen ? (
        <div className="grid grid-cols-1 gap-x-8 gap-y-1.5 text-sm tablet:grid-cols-2">
          {FLAG_WEIGHT_ROWS.map((row) => (
            <div key={row.flagType} className="flex items-start justify-between gap-3">
              <span className="flex flex-col">
                <span className={row.weight ? 'text-customLight' : 'text-customGrey'}>
                  {row.label}
                </span>
                {row.note ? <span className="text-xs text-customGrey">{row.note}</span> : null}
              </span>
              <span
                className={mergeClassNames(
                  'shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
                  row.weight >= 4 && 'bg-customRed/15 text-customRed',
                  row.weight >= 2 && row.weight < 4 && 'bg-customGold/15 text-customGold',
                  row.weight === 1 && 'bg-white/10 text-customLight',
                  row.weight === 0 && 'bg-white/5 text-customGrey'
                )}
              >
                {row.weight ? `${row.weight} pts` : 'ignored'}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const DriverBar = (props: { driver: MentorPerformanceDriverDTO }) => {
  const { driver } = props;
  const ratio = driver.maxPoints ? driver.points / driver.maxPoints : 0;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-medium text-white">{driver.label}</span>
        <span className={mergeClassNames('tabular-nums', TONE_TEXT[driver.tone])}>
          {driver.tone === 'neutral' ? 'n/a' : `${driver.points} / ${driver.maxPoints}`}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className={mergeClassNames('h-full rounded-full', TONE_BAR[driver.tone])}
          style={{ width: `${Math.round(ratio * 100)}%` }}
        />
      </div>
      <span className="text-xs text-customGrey">{driver.detail}</span>
    </div>
  );
};

const EvidenceList = (props: {
  days: number;
  mentorId: string;
  onOpen: (item: MentorPerformanceEvidenceDTO) => void;
}) => {
  const { data, isLoading } = useGetMentorPerformance(
    { days: props.days, mentorId: props.mentorId },
    { keepPreviousData: false }
  );

  const mentor = data?.mentors[0];

  if (isLoading || !mentor) {
    return (
      <div className="flex h-24 items-center justify-center">
        <LoadingSpinner size="normal" />
      </div>
    );
  }

  const groups = Object.entries(mentor.evidence ?? {}).filter(([, items]) => items.length) as [
    MentorPerformanceDriverKey,
    MentorPerformanceEvidenceDTO[]
  ][];

  if (!groups.length) {
    return (
      <span className="text-sm text-customGrey">
        Nothing to flag in this period. Every signal we track looks fine for this mentor.
      </span>
    );
  }

  const labelOf = (key: MentorPerformanceDriverKey) =>
    mentor.drivers.find((driver) => driver.key === key)?.label ?? key;

  return (
    <div className="grid grid-cols-1 gap-4 laptop:grid-cols-2">
      {groups.map(([key, items]) => (
        <div key={key} className="flex flex-col gap-2 rounded-xl bg-white/5 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
            {labelOf(key)} · {items.length}
          </span>
          <ul className="flex flex-col gap-2">
            {items.map((item, index) => {
              const clickable = !!(item.sessionId || item.enrollmentId || item.studentId);
              return (
                <li
                  key={`${key}-${index}`}
                  className={mergeClassNames(
                    'flex flex-col gap-0.5 text-sm',
                    clickable && 'cursor-pointer hover:underline'
                  )}
                  onClick={clickable ? () => props.onOpen(item) : undefined}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={mergeClassNames(
                        'h-2 w-2 shrink-0 rounded-full',
                        TONE_BAR[item.tone]
                      )}
                    />
                    <span className="text-white">{item.title}</span>
                    {item.at ? (
                      <span className="ml-auto shrink-0 text-xs text-customGrey">
                        {moment(item.at).format('D MMM YYYY')}
                      </span>
                    ) : null}
                  </span>
                  {item.detail ? (
                    <span className="pl-4 text-xs text-customGrey">{item.detail}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

const downloadCsv = (csv: string, fileName: string) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const buildCsv = (rows: MentorPerformanceDTO[], windowDays: number) =>
  stringify([
    [
      'Mentor',
      'Email',
      'Status',
      `Score (last ${windowDays} days)`,
      'Band',
      'Sessions held',
      'Meetings scheduled',
      'Active students',
      'Students lost',
      'Feedback on time %',
      'Feedback missing',
      'Parent rating',
      'Student rating',
      'AI quality',
      'Punctuality %',
      'Reliability issues',
      'Avg billable minutes',
      'Avg student churn',
      'NPS',
      'Late invoices',
      'Rejected reports',
      'Mentor changes',
      'Courses completed',
      'Courses cancelled',
      'Admission offers',
      'Admission rejections',
      'Flags raised',
      'Flag impact',
      'Strengths',
      'Weaknesses',
    ],
    ...rows.map((row) => [
      row.name,
      row.email,
      row.status ?? '',
      row.score ?? '',
      row.band ? BAND_STYLES[row.band].label : 'Not enough data',
      row.metrics.sessionsHeld,
      row.metrics.meetingsScheduled,
      row.metrics.activeStudents,
      row.metrics.studentsLost,
      row.metrics.feedbackOnTimePct ?? '',
      row.metrics.feedbackMissing,
      row.metrics.parentRating ?? '',
      row.metrics.studentRating ?? '',
      row.metrics.aiQuality ?? '',
      row.metrics.punctualityPct ?? '',
      row.metrics.reliabilityIssues,
      row.metrics.averageBillableMinutes ?? '',
      row.metrics.averageStudentChurn ?? '',
      row.metrics.nps ?? '',
      row.metrics.lateInvoices,
      row.metrics.rejectedReports,
      row.metrics.mentorChanges,
      row.metrics.coursesCompleted,
      row.metrics.coursesCancelled,
      row.metrics.admissionsAccepted,
      row.metrics.admissionsRejected,
      row.metrics.flagsRaised,
      row.metrics.flagImpact,
      row.strengths.join(' | '),
      row.weaknesses.join(' | '),
    ]),
  ]);

const sorters: Record<SortKey, (a: MentorPerformanceDTO, b: MentorPerformanceDTO) => number> = {
  score: (a, b) => {
    if (a.score === null && b.score === null) return a.name.localeCompare(b.name);
    if (a.score === null) return 1;
    if (b.score === null) return -1;
    return a.score - b.score;
  },
  name: (a, b) => a.name.localeCompare(b.name),
  sessions: (a, b) => b.metrics.sessionsHeld - a.metrics.sessionsHeld,
  students: (a, b) => b.metrics.activeStudents - a.metrics.activeStudents,
  feedback: (a, b) => (a.metrics.feedbackOnTimePct ?? 101) - (b.metrics.feedbackOnTimePct ?? 101),
  parent: (a, b) => (a.metrics.parentRating ?? 6) - (b.metrics.parentRating ?? 6),
  ai: (a, b) => (a.metrics.aiQuality ?? 6) - (b.metrics.aiQuality ?? 6),
};

const MentorPerformance = () => {
  const [days, setDays] = useState(90);
  const [band, setBand] = useState<MentorPerformanceBand | 'unscored' | 'all'>('all');
  const [status, setStatus] = useState('all');
  const [minSessions, setMinSessions] = useState(0);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [expandedId, setExpandedId] = useState<string>();

  const { data, isLoading, isFetching } = useGetMentorPerformance(
    { days },
    { keepPreviousData: true }
  );

  const {
    activeModalView,
    isModalOpen,
    mentorId,
    studentId,
    sessionId,
    enrollmentId,
    openModal,
    closeModal,
  } = useDetailsModal({ page: 'mentor' });

  const mentors = useMemo(() => data?.mentors ?? [], [data]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return mentors
      .filter((mentor) => {
        if (band === 'unscored' && mentor.score !== null) return false;
        if (band !== 'all' && band !== 'unscored' && mentor.band !== band) return false;
        if (status !== 'all' && mentor.status !== status) return false;
        if (mentor.metrics.sessionsHeld < minSessions) return false;
        if (
          needle &&
          !mentor.name.toLowerCase().includes(needle) &&
          !mentor.email.toLowerCase().includes(needle)
        )
          return false;
        return true;
      })
      .sort(sorters[sortKey]);
  }, [mentors, band, status, minSessions, search, sortKey]);

  const summary = useMemo(() => {
    const scored = mentors.filter((mentor) => mentor.score !== null);
    const average = scored.length
      ? Math.round(scored.reduce((sum, mentor) => sum + (mentor.score ?? 0), 0) / scored.length)
      : null;
    const count = (target: MentorPerformanceBand) =>
      mentors.filter((mentor) => mentor.band === target).length;
    return {
      scored: scored.length,
      unscored: mentors.length - scored.length,
      average,
      excellent: count('excellent'),
      attention: count('attention'),
      atRisk: count('at_risk'),
    };
  }, [mentors]);

  const openEvidence = (item: MentorPerformanceEvidenceDTO) => {
    if (item.sessionId) {
      openModal({ page: 'session' }, { sessionId: item.sessionId });
    } else if (item.enrollmentId) {
      openModal({ page: 'enrollment' }, { enrollmentId: item.enrollmentId });
    } else if (item.studentId) {
      openModal({ page: 'student' }, { studentId: item.studentId });
    }
  };

  if (isLoading || !data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Mentor Performance Review</h1>
      </div>

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 laptop:grid-cols-4">
        <MetricCard
          label={`Mentors scored${
            summary.unscored ? ` (${summary.unscored} without enough data)` : ''
          }`}
          value={summary.scored}
          icon={<UserGroupIcon className="h-5 w-5" />}
          accent="teal"
        />
        <MetricCard
          label="Average score"
          value={summary.average ?? '—'}
          icon={<StarIcon className="h-5 w-5" />}
          accent="lila"
        />
        <MetricCard
          label="Excellent"
          value={summary.excellent}
          icon={<ThumbUpIcon className="h-5 w-5" />}
          accent="green"
          onClick={() => setBand('excellent')}
        />
        <MetricCard
          label="Needs attention or at risk"
          value={summary.attention + summary.atRisk}
          icon={<ExclamationIcon className="h-5 w-5" />}
          accent="gold"
          onClick={() => setBand(summary.atRisk ? 'at_risk' : 'attention')}
        />
      </div>

      <FlagWeightsLegend />

      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 laptop:flex-row laptop:items-end laptop:justify-between">
          <SectionHeading
            title={`${filtered.length} of ${mentors.length} mentors`}
            description={`Computed ${moment(data.generatedAt).fromNow()} over the last ${
              data.windowDays
            } days.${isFetching ? ' Refreshing…' : ''}`}
          />
          <Button
            variant="secondary"
            buttonText="Export CSV"
            icon={<DownloadIcon className="h-4 w-4" />}
            disabled={!filtered.length}
            onClick={() =>
              downloadCsv(
                buildCsv(filtered, data.windowDays),
                `mentor-performance-${moment().format('YYYY-MM-DD')}.csv`
              )
            }
          />
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <FilterField width="w-full tablet:w-72">
            <AgoraSearchBar
              search={search}
              handleSearch={setSearch}
              placeholder="Search mentor by name or email..."
            />
          </FilterField>
          <FilterField width="w-full tablet:w-44">
            <Select
              label="Period"
              variant="secondary"
              value={days}
              options={WINDOW_OPTIONS}
              onSelect={(value) => {
                setDays(Number(value));
                setExpandedId(undefined);
              }}
            />
          </FilterField>
          <FilterField width="w-full tablet:w-48">
            <Select
              label="Band"
              variant="secondary"
              value={band}
              options={BAND_OPTIONS}
              onSelect={(value) => setBand(value as typeof band)}
            />
          </FilterField>
          <FilterField width="w-full tablet:w-48">
            <Select
              label="Mentor status"
              variant="secondary"
              value={status}
              options={STATUS_OPTIONS}
              onSelect={(value) => setStatus(String(value))}
            />
          </FilterField>
          <FilterField width="w-full tablet:w-44">
            <Select
              label="Activity"
              variant="secondary"
              value={minSessions}
              options={MIN_SESSION_OPTIONS}
              onSelect={(value) => setMinSessions(Number(value))}
            />
          </FilterField>
          <FilterField width="w-full tablet:w-52">
            <Select
              label="Sort by"
              variant="secondary"
              value={sortKey}
              options={SORT_OPTIONS}
              onSelect={(value) => setSortKey(value as SortKey)}
            />
          </FilterField>
        </div>

        {!filtered.length ? (
          <EmptyState>No mentors match the current filters.</EmptyState>
        ) : (
          <TableShell minWidth="min-w-[1440px]">
            <thead>
              <tr className={headRow}>
                <th className={leftHead}>Mentor</th>
                <th className={leftHead}>Score</th>
                <th className={rightHead}>Sessions</th>
                <th className={rightHead}>Students</th>
                <th className={rightHead}>Feedback on time</th>
                <th className={rightHead}>Parent ★</th>
                <th className={rightHead}>Student ★</th>
                <th className={rightHead}>AI quality</th>
                <th className={rightHead}>On time</th>
                <th className={rightHead} title="Mentor cancellations, reschedules and absences">
                  Issues
                </th>
                <th className={rightHead}>Finished</th>
                <th className={rightHead}>Admissions</th>
                <th className={rightHead} title="Flags raised · impact points">
                  Flags
                </th>
                <th className={leftHead}>Biggest concern</th>
                <th className="w-10 px-2 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const expanded = expandedId === row.mentorId;
                const worst = row.drivers
                  .filter((driver) => driver.tone === 'bad')
                  .sort((a, b) => a.points / a.maxPoints - b.points / b.maxPoints)[0];
                const toggle = () => setExpandedId(expanded ? undefined : row.mentorId);

                return (
                  <Fragment key={row.mentorId}>
                    <tr
                      className={mergeClassNames(
                        bodyRow,
                        'cursor-pointer hover:bg-white/5',
                        expanded && 'bg-white/5'
                      )}
                      onClick={toggle}
                    >
                      <td className={leftCell}>
                        <div className="flex flex-col">
                          <button
                            type="button"
                            className="text-left font-semibold text-white hover:underline"
                            onClick={(event) => {
                              event.stopPropagation();
                              openModal({ page: 'mentor' }, { mentorId: row.mentorId });
                            }}
                          >
                            {row.name}
                          </button>
                          <span className="text-xs text-customGrey">
                            {row.status ?? 'No status'}
                          </span>
                        </div>
                      </td>
                      <td className={leftCell}>
                        <ScoreBadge score={row.score} band={row.band} />
                      </td>
                      <td className={rightCell}>{row.metrics.sessionsHeld}</td>
                      <td className={rightCell}>
                        {row.metrics.activeStudents}
                        {row.metrics.studentsLost ? (
                          <span className="text-customRed"> −{row.metrics.studentsLost}</span>
                        ) : null}
                      </td>
                      <td className={rightCell}>
                        {formatPct(row.metrics.feedbackOnTimePct)}
                        {row.metrics.feedbackMissing ? (
                          <span className="text-customRed">
                            {' '}
                            ({row.metrics.feedbackMissing} missing)
                          </span>
                        ) : null}
                      </td>
                      <td className={rightCell}>{formatRating(row.metrics.parentRating)}</td>
                      <td className={rightCell}>{formatRating(row.metrics.studentRating)}</td>
                      <td className={rightCell}>{formatRating(row.metrics.aiQuality)}</td>
                      <td className={rightCell}>{formatPct(row.metrics.punctualityPct)}</td>
                      <td className={rightCell}>{row.metrics.reliabilityIssues}</td>
                      <td className={rightCell}>
                        <span className="text-customGreen">{row.metrics.coursesCompleted}</span>
                        <span className="text-customGrey"> / </span>
                        <span className="text-customRed">{row.metrics.coursesCancelled}</span>
                        {row.metrics.mentorChanges ? (
                          <span className="text-xs text-customGold">
                            {' '}
                            · {row.metrics.mentorChanges} moved
                          </span>
                        ) : null}
                      </td>
                      <td className={rightCell}>
                        <span className="text-customGreen">{row.metrics.admissionsAccepted}</span>
                        <span className="text-customGrey"> / </span>
                        <span className="text-customRed">{row.metrics.admissionsRejected}</span>
                      </td>
                      <td className={rightCell}>
                        {row.metrics.flagsRaised}
                        {row.metrics.flagImpact ? (
                          <span
                            className={mergeClassNames(
                              'text-xs',
                              row.metrics.flagImpact >= 10 ? 'text-customRed' : 'text-customGold'
                            )}
                          >
                            {' '}
                            · {row.metrics.flagImpact} pts
                          </span>
                        ) : null}
                      </td>
                      <td className={mergeClassNames(leftCell, 'max-w-xs text-xs')}>
                        {worst ? (
                          <span className="text-customRed">{worst.detail}</span>
                        ) : row.score === null ? (
                          <span className="text-customGrey">Too little activity to judge</span>
                        ) : (
                          <span className="text-customGreen">Nothing stands out</span>
                        )}
                      </td>
                      <td className="px-2 py-3 text-customGrey">
                        {expanded ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </td>
                    </tr>

                    {expanded ? (
                      <tr className={bodyRow}>
                        <td colSpan={12} className="bg-black/20 px-6 py-5">
                          <div className="flex flex-col gap-6">
                            <div className="grid grid-cols-1 gap-4 laptop:grid-cols-2">
                              <div className="flex flex-col gap-2 rounded-xl bg-customGreen/5 p-4">
                                <span className="text-xs font-semibold uppercase tracking-wide text-customGreen">
                                  Doing well
                                </span>
                                {row.strengths.length ? (
                                  <ul className="flex list-disc flex-col gap-1 pl-4 text-sm text-customLight">
                                    {row.strengths.map((item) => (
                                      <li key={item}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="text-sm text-customGrey">
                                    No signal is clearly above the bar yet.
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col gap-2 rounded-xl bg-customRed/5 p-4">
                                <span className="text-xs font-semibold uppercase tracking-wide text-customRed">
                                  Needs work
                                </span>
                                {row.weaknesses.length ? (
                                  <ul className="flex list-disc flex-col gap-1 pl-4 text-sm text-customLight">
                                    {row.weaknesses.map((item) => (
                                      <li key={item}>{item}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="text-sm text-customGrey">
                                    No signal is clearly below the bar.
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col gap-3">
                              <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
                                Score breakdown · {row.earnedPoints} of {row.availablePoints} points
                              </span>
                              <div className="grid grid-cols-1 gap-x-8 gap-y-4 tablet:grid-cols-2 laptop:grid-cols-3">
                                {row.drivers.map((driver) => (
                                  <DriverBar key={driver.key} driver={driver} />
                                ))}
                              </div>
                            </div>

                            <div className="flex flex-col gap-3">
                              <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
                                Evidence
                              </span>
                              <EvidenceList
                                days={days}
                                mentorId={row.mentorId}
                                onOpen={openEvidence}
                              />
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </TableShell>
        )}
      </section>

      {isModalOpen && (
        <DetailsModal
          {...activeModalView}
          mentorId={mentorId}
          studentId={studentId}
          enrollmentId={enrollmentId}
          sessionId={sessionId}
          isOpen={true}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default MentorPerformance;
